<script lang="ts" setup>
import { computed } from "vue";

import type { ClassQuizPlayer } from "@/common/types/vocab";
import { formatName } from "@/common/utils";

const props = defineProps<{
  topPlayers: ClassQuizPlayer[];
}>();

const firstPlace = computed(() => formatName(props.topPlayers[0]?.name));
const secondPlace = computed(() => formatName(props.topPlayers[1]?.name));
const thirdPlace = computed(() => formatName(props.topPlayers[2]?.name));
</script>

<template>
  <div class="container d-flex justify-content-center">
    <div class="podium">
      <div v-if="secondPlace" class="podium__second-place">
        <img src="/assets/img/vocab/quiz/silver.png" alt="" class="podium__bar-decoration" />
        <div class="podium__bar">2</div>
      </div>
      <div v-if="firstPlace" class="podium__first-place">
        <img src="/assets/img/vocab/quiz/gold.png" alt="" class="podium__bar-decoration" />
        <div class="podium__bar">1</div>
      </div>
      <div v-if="thirdPlace" class="podium__third-place">
        <img src="/assets/img/vocab/quiz/bronze.png" alt="" class="podium__bar-decoration" />
        <div class="podium__bar">3</div>
      </div>

      <div v-if="secondPlace" class="podium__name-wrapper podium__name-wrapper--second-place">
        <p class="podium__name">{{ secondPlace }}</p>
      </div>
      <div v-if="firstPlace" class="podium__name-wrapper podium__name-wrapper--first-place">
        <p class="podium__name">{{ firstPlace }}</p>
      </div>
      <div v-if="thirdPlace" class="podium__name-wrapper podium__name-wrapper--third-place">
        <p class="podium__name">{{ thirdPlace }}</p>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.podium {
  display: grid;
  grid-template-columns: repeat(3, 160px);
  gap: 20px 30px;
  align-items: end;

  &__name-wrapper {
    display: flex;
    grid-row: 2;
    align-items: center;
    justify-content: center;
    height: 100%;

    &--second-place {
      grid-column: 1;
    }

    &--first-place {
      grid-column: 2;
    }

    &--third-place {
      grid-column: 3;
    }
  }

  &__name {
    margin-bottom: 0;
    font-size: 1.5rem;
    font-weight: 500;
    line-height: 1.75rem;
    text-align: center;
  }
}

.podium__first-place {
  grid-column: 2;
  grid-row: 1;

  & > .podium__bar {
    height: 300px;
    line-height: 300px;
  }

  & > .podium__bar-decoration {
    position: relative;
    top: 20px;
    left: 22px;
  }
}

.podium__second-place {
  grid-column: 1;
  grid-row: 1;

  & > .podium__bar {
    height: 225px;
    line-height: 275px;
  }

  & > .podium__bar-decoration {
    position: relative;
    top: 75px;
    left: 47px;
  }
}

.podium__third-place {
  grid-column: 3;
  grid-row: 1;

  & > .podium__bar {
    height: 175px;
    line-height: 225px;
  }

  & > .podium__bar-decoration {
    position: relative;
    top: 75px;
    left: 47px;
  }
}

.podium__bar {
  width: 160px;
  border-radius: 8px;
  background-color: rgb(@theme-colors[@secondary][base]);
  color: @colors[@white];
  font-size: 2.75rem;
  font-weight: 700;
  text-align: center;
}
</style>
